import { useMemo, useState } from 'react'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useAppStore } from '../../store/app'
import { wsRequest } from '../../api/ws'
import { NavBar, NavBarButton } from '../../components/NavBar'
import './AgentMandateScreen.css'

export function AgentMandateScreen({
  title,
  mandate,
  jobDescriptionID,
  requiredSkills,
}: {
  title: string
  mandate: string
  // When present the mandate is editable; saving it may trigger the Onboarding
  // Facilitator to propose a role-change delta (JD-3 / onboarding.md §6.6).
  jobDescriptionID?: string
  requiredSkills?: string
}) {
  const popNav = useAppStore((s) => s.popNav)
  const editable = !!jobDescriptionID

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(mandate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const html = useMemo(() => {
    if (!mandate) return ''
    const raw = marked.parse(mandate, { async: false }) as string
    return DOMPurify.sanitize(raw)
  }, [mandate])

  async function save() {
    if (!jobDescriptionID || saving) return
    setSaving(true)
    setError(null)
    try {
      const resp = await wsRequest<{ agent_job_description?: { id: string }; error?: string }>(
        'update_agent_job_description',
        { id: jobDescriptionID, title, mandate: draft, required_skills: requiredSkills ?? '' },
        'agent_job_description_updated',
        { kind: 'agent_job_description', id: jobDescriptionID, action: 'update' },
      )
      if (resp.error) {
        setError(resp.error)
        setSaving(false)
        return
      }
      // Saved. The server may now task the Facilitator to propose a role-change
      // delta, which surfaces in the review queue. Return to settings.
      popNav()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setSaving(false)
    }
  }

  const trailing = editable ? (
    editing ? (
      <NavBarButton icon={saving ? '…' : '✓'} label="Save" onClick={() => void save()} />
    ) : (
      <NavBarButton
        icon="✎"
        label="Edit"
        onClick={() => {
          setDraft(mandate)
          setEditing(true)
        }}
      />
    )
  ) : undefined

  return (
    <div className="agent-mandate">
      <NavBar
        title={title || 'Mandate'}
        leading={<NavBarButton icon="‹" label="Back" onClick={popNav} />}
        trailing={trailing}
      />
      <div className="agent-mandate__body">
        {editing ? (
          <>
            <textarea
              className="agent-mandate__editor"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="What is this role responsible for?"
              autoFocus
              disabled={saving}
            />
            {error && <div className="agent-mandate__error">{error}</div>}
            <p className="agent-mandate__hint">
              Saving an updated role can prompt your Onboarding Facilitator to
              suggest access changes for this agent — you'll review them before
              anything happens.
            </p>
          </>
        ) : mandate ? (
          <div className="agent-mandate__md" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="agent-mandate__empty">No mandate provided.</div>
        )}
      </div>
    </div>
  )
}
